/**
 * The generic Form engine — RHF + Zod, driven by the semantic layer (field_meta
 * via buildDetailView). ALWAYS-EDITABLE (no view/edit toggle — the D365 doctrine),
 * with dirty tracking: on an EXISTING record the save/discard bar appears only
 * when something changed. On a CREATE form (isNew) it is shown from the start —
 * its whole purpose is to save a new record, so the save action must always be
 * reachable. (Previously it relied on the form reading dirty, which only held
 * under the dev server's StrictMode double-invoke and vanished in a prod build.)
 * Everything below is derived from the dictionary — sections, controls,
 * required markers, validation (min/max/length/pattern), read-only. One form
 * serves every entity; enriching field_meta sharpens it with no code change.
 */
import { useId, useMemo, useRef, type FormEvent, type ReactNode } from 'react'
import { flushSync } from 'react-dom'
import { useForm, useWatch, Controller, type Resolver, type DefaultValues } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { type FormView, type Column } from '@solum/experience'
import type { Row } from '@solum/sdk'
import { Button, Field, FieldEditor, FormLayoutSections, Input, SaveBar, fieldEditorBinding, safeHttpUrl, useUnsavedChanges, type FieldAction } from '@solum/ui'
import { confirmedWrite } from '../data/writeState'
import s from './EntityForm.module.css'

type FormValues = Record<string, unknown>

/** A successful write's destination. Enter and ordinary form submit mean stay. */
export type SaveIntent = 'stay' | 'close'

/** The clicked native submit button carries the exceptional destination.
 * Missing submitters (including implicit Enter submission) always mean stay. */
export function saveIntentOf(event: unknown): SaveIntent {
  const submitter = (event as {
    nativeEvent?: { submitter?: { getAttribute?: (name: string) => string | null } | null }
  } | undefined)?.nativeEvent?.submitter
  return submitter?.getAttribute?.('name') === 'save-intent'
    && submitter.getAttribute('value') === 'close'
    ? 'close'
    : 'stay'
}

/**
 * A value the caller never supplied is OMITTED from the patch, not sent empty.
 *
 * Two rules, both explicit on purpose (#1579):
 *
 *  - `undefined` is never copied. It used to be, and the transport happened to
 *    drop it because `JSON.stringify` omits undefined members - a contract that
 *    held by accident and would have changed the moment anything serialised
 *    differently.
 *  - `omitEmpty` names the columns the SERVER fills when the statement omits
 *    them. On a create form a blank one is dropped rather than sent as `null`
 *    or `''`: sending either OVERRIDES the database default, which is how a
 *    lead came to need `Status = New` typed into it by hand when
 *    `crm.lead.status` already defaults to `new`. `''` counts as blank because
 *    a text input that was cleared and one never touched are the same intent,
 *    and an empty string is not a legal option value anyway.
 *
 * It never applies to an EXISTING record: there, clearing a field is a real
 * edit and the caller means it.
 */
export function formSavePatch(
  editable: Pick<Column, 'field'>[],
  hiddenFields: string[],
  values: FormValues,
  omitEmpty: ReadonlySet<string> = new Set(),
  emptyAsNull: ReadonlySet<string> = new Set(),
): Row {
  const omit = omitEmpty
  const blank = (v: unknown) => v === undefined || v === null || v === ''
  const patch: Row = {}
  const put = (field: string) => {
    const v = values[field]
    if (v === undefined) return
    if (omit.has(field) && blank(v)) return
    if (emptyAsNull.has(field) && v === '') {
      patch[field] = null
      return
    }
    patch[field] = v
  }
  for (const field of editable) put(field.field)
  for (const field of hiddenFields) put(field)
  return patch
}

/**
 * The columns THIS render will let the server fill: on a create form, the ones
 * the database populates when the statement omits them.
 *
 * ONE list, three consumers — the Zod schema, the save patch, and the required
 * marker. They were three separate `isNew &&` decisions at three call sites, and
 * a mutation run found the consequence: turning the schema's copy off was
 * INVISIBLE to every test, because this package has no jsdom and a resolver only
 * runs on submit. Deriving all three from one value moves that call-site risk
 * onto a decision the RENDER exercises, which the tests can see.
 *
 * `required` still means NOT NULL — it is `core.field_meta.required`, read by
 * this file's resolver and by nothing on the server: there is no
 * `core.validate_record`, three migration headers naming one notwithstanding.
 * That is why this distinction can only be drawn here.
 */
export interface CreateMode {
  /** The columns this render lets the server fill. Empty on an existing record. */
  serverFilled: ReadonlySet<string>
  /** The resolver schema, with those columns not required. */
  schema: z.ZodType<FormValues>
  /** The save patch, with untouched ones omitted rather than sent blank. */
  toPatch: (values: FormValues) => Row
}

/**
 * ONE decision, ONE call site, three consumers — the required marker, the
 * resolver and the save patch.
 *
 * It is a factory rather than three arguments because of what a mutation run
 * measured. This package has no jsdom, so a resolver and a save patch only run
 * on submit and a unit test cannot reach them: with the three consumers taking
 * their own `isNew`, mutating the SCHEMA's copy to `[]` and mutating the
 * PATCH's copy to `[]` both came back GREEN — the create form would have gone
 * back to blocking Save on a server-defaulted blank, and to sending `''` over
 * the top of the database default, with nothing to notice. Rule 10 calls a
 * green mutation a finding, and the finding was that two of the three call
 * sites were invisible.
 *
 * Collapsing them leaves exactly one argument to get wrong — `isNew`, here —
 * and the RENDER depends on it too, so the render assertions cover it. That is
 * the same move `#877` made when it pulled the submit decision out into
 * `data/writeState.ts`'s `confirmedWrite`: put the decision where a test can
 * reach it, rather than asserting a proxy for it.
 */
export function createMode(editable: Column[], hiddenFields: string[], isNew: boolean): CreateMode {
  const fields = isNew ? editable.filter((f) => f.hasServerDefault).map((f) => f.field) : []
  const serverFilled = new Set(fields)
  /* Browser controls express "nothing selected" as an empty string. That is a
     valid value for text, but PostgreSQL date, numeric, option and reference
     columns use NULL for absence. On create, omission also preserves a server
     default; on edit, NULL is the explicit clearing act. One set keeps the
     form, grid and future typed-editor registry from growing per-entity fixes. */
  const typedBlankFields = new Set(editable
    .filter((f) => ['date', 'ref', 'option', 'number', 'money', 'percent'].includes(f.kind))
    .map((f) => f.field))
  const omittedOnCreate = isNew
    ? new Set([...serverFilled, ...typedBlankFields])
    : serverFilled
  return {
    serverFilled,
    schema: schemaFor(editable, hiddenFields, serverFilled),
    toPatch: (values) => formSavePatch(
      editable,
      hiddenFields,
      values,
      omittedOnCreate,
      isNew ? new Set() : typedBlankFields,
    ),
  }
}

function fieldSchema(f: Column, serverFilled: ReadonlySet<string>): z.ZodTypeAny {
  const r = f.rules
  // A server-filled column is not required ON CREATE. Blocking Save on it is
  // the console asserting a rule the database does not have.
  const required = f.required && !serverFilled.has(f.field)
  if (f.kind === 'boolean') return z.boolean().optional()
  /* A SET of governed keys (#1928). Without this arm the fallthrough below
     validates an array with `z.string()`, so the control renders, the chips
     commit, and SAVE silently refuses with "Expected string, received array"
     against a field the user can see is filled — the worst of the three
     possible failures, because nothing on screen names the field.

     `required` is honoured as NON-EMPTY rather than as "present": the one
     column carrying this kind is `not null default '{}'`, so an empty array
     satisfies NOT NULL perfectly and a required multiselect asserting only
     presence would assert nothing at all. */
  if (f.kind === 'multiselect') {
    const a = z.array(z.string())
    return required ? a.min(1, 'Choose at least one') : a.optional()
  }
  if (f.kind === 'money' || f.kind === 'number' || f.kind === 'percent') {
    let n = z.coerce.number()
    if (typeof r.min === 'number') n = n.min(r.min)
    if (typeof r.max === 'number') n = n.max(r.max)
    /* Literal first: `z.coerce.number()` turns browser blank into zero. Blank
       and zero are different business facts, so preserve the blank for the
       create/update normalizer above. */
    return required
      ? z.preprocess((value) => value === '' ? undefined : value, n)
      : z.union([z.literal(''), n]).optional()
  }
  let str = z.string()
  if (typeof r.maxLength === 'number') str = str.max(r.maxLength)
  if (typeof r.minLength === 'number') str = str.min(r.minLength)
  if (typeof r.pattern === 'string') str = str.regex(new RegExp(r.pattern), 'Invalid format')
  if (required) str = str.min(1, 'Required')
  const presented = f.format === 'url'
    ? str.refine((value) => value === '' || safeHttpUrl(value) != null,
      'Enter an absolute http:// or https:// URL')
    : str
  if (required) return presented

  /* Older registrations use `text` for PostgreSQL text[] columns because the
     metamodel had no set kind until #1928. An EDIT of another field must carry
     those untouched server values through: rejecting an existing string[] in
     the resolver prevents submit before the governed write is even called.

     This is deliberately the optional/plain-text arm only. A required scalar
     still cannot be satisfied by an array, and an edited native input still
     produces a string. The compatibility arm therefore preserves values the
     server supplied without turning arrays into a second authoring syntax. */
  return z.union([presented, z.array(z.string())]).optional().or(z.literal(''))
}

/**
 * Exported for `EntityForm.serverDefault.test.tsx`. This package has no jsdom,
 * so a form cannot be SUBMITTED in a unit test — and "does the resolver still
 * block Save on a blank the server will fill?" is a question about the schema,
 * not about the DOM. It takes the same `serverFilledFields` list the render and
 * the save patch take, so the argument this function receives is one the
 * rendered output also depends on.
 */
export function schemaFor(
  editable: Column[], hiddenFields: string[], serverFilled: ReadonlySet<string> = new Set(),
): z.ZodType<FormValues> {
  const shape: Record<string, z.ZodTypeAny> = {}
  for (const f of editable) shape[f.field] = fieldSchema(f, serverFilled)
  for (const field of hiddenFields) shape[field] = z.any().optional()
  return z.object(shape) as unknown as z.ZodType<FormValues>
}

const tagOf = (f: Column) => (f.classification === 'pii' ? 'PII' : f.classification === 'confidential' ? 'CONF' : null)

export function EntityForm({ view, row, onSave, afterSave, saving, isNew = false, readOnly = false, notice, refPicker, fieldActions, permittedCapabilities = null, hiddenFields = [], aside }: {
  view: FormView
  row: Row
  /** The one governed write door. Both save intents pass through this callback. */
  onSave: (patch: Row) => Promise<unknown>
  /** Runs only after the server accepted the write and RHF confirmed it. */
  afterSave?: (result: unknown, intent: SaveIntent) => void | Promise<void>
  saving: boolean
  isNew?: boolean
  /**
   * Renders a `kind === 'ref'` column as a picker. A hidden/missing target is
   * supplied as a disabled picker; a raw identifier input is never a safe fallback.
   * See `form/RefPicker.tsx`.
   */
  /**
   * ACCESSORY offers for a text or multiline column (#1679), INJECTED for the
   * same reason `refPicker` is: this file stays free of data hooks, and the
   * one place that knows a knowledge article's body can take a figure is the
   * record surface, not the generic form.
   *
   * Returning undefined for a column is the ordinary answer, and it costs
   * nothing — `FieldEditor` emits byte-identical markup for a field that offers
   * nothing.
   */
  fieldActions?: (field: string) => readonly FieldAction[] | undefined
  /** What the host permits. `null` = no capability model — see `FieldAction`. */
  permittedCapabilities?: readonly string[] | null
  refPicker?: (
    f: Column,
    value: string | null,
    onChange: (patch: Row) => void,
    values: FormValues,
  ) => ReactNode
  /** Composite relation discriminators live in form state and the save patch,
   *  but never render as a second independently editable field. */
  hiddenFields?: string[]
  /**
   * A host panel rendered inside the form, given the LIVE form values (#2276).
   *
   * Injected for the same reason `refPicker` and `fieldActions` are: this file
   * stays free of data hooks, and the thing that knows an address can be drawn
   * on a map is the record surface, not the generic form.
   *
   * It receives `values`, not `row`, and that is the whole point. The map
   * preview then follows uncommitted edits with no autosave anywhere — because
   * it renders from form state, it cannot write — and **Cancel restores the
   * persisted location for free**: `onDiscard` already calls `reset(row)`, so
   * the values this panel reads snap back with every other field. A panel given
   * a copy of the row, or its own draft state, would have needed a second
   * rollback path and would eventually have disagreed with the form about what
   * is saved.
   */
  aside?: (values: FormValues) => ReactNode
  /**
   * The whole record is read-only, whatever field_meta says about the individual
   * columns — the `write_door: 'rpc'` case (#877). Every control renders
   * read-only and there is no save bar at all, because a Save here is answered
   * `403 permission denied` for every user: `core.enforce_write_doors()` has
   * revoked the table's DML, so this is not a permission somebody could be
   * granted. The caller says WHY, above, via `notice`.
   */
  readOnly?: boolean
  /** Rendered above the save bar — the refusal from the last attempt, or why there is no Save. */
  notice?: ReactNode
}) {
  const formControlId = useId().replace(/:/g, '')
  const hidden = useMemo(() => new Set(hiddenFields), [hiddenFields])
  const editable = useMemo(
    () => (readOnly ? [] : view.sections.flatMap((s) => s.fields).filter((f) => !f.readOnly && !hidden.has(f.field))),
    [view.sections, readOnly, hidden])
  /* THE one create-mode decision. The required marker below, the resolver and
     the save patch all read it, so no consumer can disagree with another — and
     the only argument that can be got wrong is `isNew`, which the render
     depends on and the tests therefore see. */
  const mode = useMemo(
    () => createMode(editable, readOnly ? [] : hiddenFields, isNew),
    [editable, hiddenFields, readOnly, isNew])
  const { serverFilled, schema } = mode

  const { control, register, handleSubmit, reset, setValue, formState } = useForm<FormValues>({
    resolver: zodResolver(schema) as Resolver<FormValues>,
    defaultValues: row as DefaultValues<FormValues>,
  })
  const values = useWatch({ control }) as FormValues
  useUnsavedChanges(`entity-form:${view.entity}:${String(row.id ?? 'new')}`, formState.isDirty)
  const saveInFlight = useRef(false)

  const saveValues = async (values: FormValues, intent: SaveIntent) => {
    const patch = mode.toPatch(values)
    // `reset(values)` is the form CLAIMING the save landed — it clears the dirty
    // state and takes the "Unsaved changes" bar down. `confirmedWrite` is the
    // one place that decides "confirm iff the server took it", shared with the
    // list's cell flash so the two cannot drift (#877).
    //
    // On `main` the rejection PROPAGATED out of here instead. That happened to
    // keep the bar up — for the wrong reason, as an unhandled promise rejection
    // nothing was waiting for, with nothing on screen. It is handled now, the
    // bar stays because staying is CORRECT, and the caller renders the reason
    // through `notice`.
    let result: unknown
    const written = await confirmedWrite(
      async () => { result = await onSave(patch) },
      // Publish the acknowledged clean state before a host navigates. A
      // queued React reset otherwise leaves the shell's dirty guard active.
      () => flushSync(() => reset(values)),
    )
    if (written) await afterSave?.(result, intent)
  }
  /* Both visible save controls are native submits through this ONE RHF
     validation/write pipeline. Only the clicked close submitter carries the
     exceptional destination; Enter and every submit without one mean STAY. */
  const validatedSubmit = handleSubmit((values, event) => saveValues(values, saveIntentOf(event)))
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    // Acquire before asynchronous validation, not after the saving prop's
    // next render: rapid click/Enter submits must share one write.
    if (saveInFlight.current || saving) return
    saveInFlight.current = true
    try { await validatedSubmit(event) }
    finally { saveInFlight.current = false }
  }

  /**
   * ONE control per kind, from `@solum/ui` (#1679 slice 2).
   *
   * This was a per-kind if-chain of ~65 lines. `crm/RecordPage.tsx` has a
   * SECOND one over the same `core.field_meta.kind` vocabulary, and the two
   * were kept in step by a source-text test that greps both files
   * (`optionSet.test.tsx:242-271`) — a fair mechanism when there is nothing
   * shared to point at, and unnecessary once there is.
   *
   * The BINDING split is unchanged and is now asked rather than assumed:
   * `fieldEditorBinding` returns `native` exactly where the leaf is a real
   * `<input>` and RHF's ref can land on it, and `controlled` exactly where the
   * leaf is a Radix or custom component that takes no ref and no DOM change
   * event. Wrapping the native ones in `Controller` would have been a
   * behaviour change — a re-render per keystroke and a different SSR shape,
   * which `EntityForm.test.tsx:73-79` counts literally — so it is not done.
   *
   * `optionLabel` is still resolved for a read-only option; it happens inside
   * `FieldEditor` now, which is what makes the grep test replaceable rather
   * than merely broken.
   */
  const control_ = (f: Column) => {
    const spec = {
      kind: f.kind,
      format: f.format,
      name: f.field,
      label: f.label,
      options: f.options,
      // A server-filled column is not required ON CREATE — the console must not
      // assert a rule the database does not have.
      required: f.required && !serverFilled.has(f.field),
      readOnly: f.readOnly,
      /* The column's own `core.field_meta.rules`, forwarded rather than
         re-derived. `EntityForm` already reads `f.rules` for the validation
         schema (`fieldSchema`, :143); the editor needs the same row to know a
         policy body from a title. */
      rules: f.rules,
    }
    // The `option` placeholder is the one piece of copy `serverFilled` changes.
    const placeholder = f.kind === 'option' && serverFilled.has(f.field)
      ? 'Use server default'
      : undefined

    /* The accessory seam, on the NATIVE binding only, and that is #1679's shape
       rather than an omission: `FieldEditor` renders `FieldActions` beside its
       text/multiline/numeric LEAF, and option, boolean, ref and date all return
       before reaching it. Those four are exactly what `fieldEditorBinding` calls
       `controlled`, so forwarding to the branch below would be dead code —
       measured, by writing the assertion and watching it fail.

       The native branch is also the one that matters: a multiline body is
       native, so wiring only the controlled branch would have shipped a seam
       that typechecked, unit-tested green against its hook, and never rendered
       a control on the one field it was built for.

       `onActionCommit` is deliberately NOT `field.onChange`: a native field is
       bound by `register()` and has no `onChange` here at all, and the write has
       to keep THIS form's dirty and validation state honest. That is
       `setValue(..., { shouldDirty: true, shouldValidate: true })`, which is
       what `FieldEditor`'s own docstring asks the host for. */
    const accessory = {
      actions: fieldActions?.(f.field),
      permittedCapabilities,
      onActionCommit: (next: string) => {
        setValue(f.field, next, { shouldDirty: true, shouldValidate: true })
      },
    }

    if (fieldEditorBinding(spec, readOnly) === 'native') {
      return <FieldEditor field={spec} readOnly={readOnly} native={register(f.field)} {...accessory} />
    }

    return (
      <Controller control={control} name={f.field} render={({ field }) => (
        <FieldEditor
          field={spec}
          readOnly={readOnly}
          value={field.value}
          onChange={field.onChange}
          onBlur={field.onBlur}
          id={f.kind === 'date' ? `${formControlId}-${f.field}` : undefined}
          placeholder={placeholder}
          /* A REFERENCE is picked, not typed, and the branch is INJECTED so
             `EntityForm` stays free of data hooks — the fixture surfaces render
             it with no server, and the one place that knows what a ref column
             points at is the metamodel derivation, not this file. A composite
             ref writes TWO columns, one of them hidden, which `field.onChange`
             cannot do alone; that is why the picker gets `setValue`. */
          slot={f.kind === 'ref' && refPicker
            ? refPicker(f, (field.value as string) ?? null, (valuePatch) => {
                for (const [name, value] of Object.entries(valuePatch)) {
                  setValue(name, value ?? '', { shouldDirty: true, shouldValidate: true })
                }
              }, values)
            : undefined}
        />
      )} />
    )
  }

  return (
    <form className={s.form} onSubmit={submit}>
      {/* Browsers resolve implicit Enter submission through the first submit
          control. Keep that native default explicit and non-interactive; the
          visible primary action below carries the same `stay` value. */}
      {afterSave && <Button type="submit" name="save-intent" value="stay" hidden aria-hidden="true" tabIndex={-1} />}
      {/* #2009 — geometry and section semantics come from the SHARED renderer,
          which Portal mounts too. This file keeps what is genuinely console:
          react-hook-form controls, validation messages and the governed write
          door. The fieldset/legend/grid and the profile decision are no longer
          a console opinion, which is what stopped a configured tablet span
          rendering differently in the two hosts. */}
      <FormLayoutSections
        sections={view.sections.map((sec) => ({
          ...sec,
          fields: sec.fields.filter((f) => !hidden.has(f.field)),
        }))}
        renderField={(f) => (
          <Field label={f.label} tag={tagOf(f)}
            htmlFor={f.kind === 'date' ? `${formControlId}-${f.field}` : undefined}
            required={f.required && !f.readOnly && !serverFilled.has(f.field)}
            /* Not silence: a column that WOULD have been marked required
               says what happens if it is left alone, so a missing asterisk
               reads as a decision rather than as missing metadata.
               Rendered through `hint`, so it is announced with the
               control rather than only seen. */
            hint={f.required && !f.readOnly && serverFilled.has(f.field)
              ? 'Use server default'
              : undefined}
            error={formState.errors[f.field]?.message as string | undefined}>
            {control_(f)}
          </Field>
        )} />
      {!readOnly && hiddenFields.map((field) => <Input key={field} type="hidden" {...register(field)} />)}

      {/* The host's panel, INSIDE the form and after the fields, so at narrow
          widths it stacks beneath them rather than competing with the field
          grid — the layout decision stays the form's (#2276). */}
      {aside?.(values)}

      {notice}

      {/* No save bar at all when the door is shut — a Save that is answered 403
          for every user is not a control, it is a claim (#877). The dirty bar
          otherwise STAYS up after a failed save, which is correct: the changes
          really are unsaved. `notice` is what makes that state legible; on
          `main` the bar stayed with nothing said. */}
      {!readOnly && (formState.isDirty || isNew) && (
        <SaveBar
          state={isNew && !formState.isDirty ? 'new' : 'dirty'}
          saving={saving || formState.isSubmitting}
          saveAndClose={afterSave != null}
          onDiscard={() => reset(row as DefaultValues<FormValues>)}
        />
      )}
    </form>
  )
}
